import { useMemo, useState } from "react";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import { detectPlatform } from "@/lib/downloads";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Normalize the download-platform key (mac-arm64, mac-x64, linux, windows)
// down to what the waitlist cares about.
function normalizePlatform(p) {
  if (p === "linux") return "linux";
  if (p === "windows") return "windows";
  if (p && p.startsWith("mac")) return "mac";
  return "other";
}

const BUILD_LABEL = {
  linux: "the Linux build",
  windows: "the Windows build",
  mac: "new builds",
  other: "new builds",
};

// Email capture for visitors who can't install today (non-macOS). Posts to
// /api/waitlist, which notifies the owner and confirms to the subscriber.
export const WaitlistForm = ({ platform, location = "faq" }) => {
  const key = useMemo(
    () => normalizePlatform(platform ?? detectPlatform()),
    [platform],
  );
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading" || status === "done") return;

    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setStatus("error");
      setError("Enter a valid email address.");
      return;
    }

    setStatus("loading");
    setError("");
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: value, platform: key }),
      });
      if (!res.ok) throw new Error("request_failed");
      setStatus("done");
      trackEvent("waitlist_signup", { location, platform: key });
    } catch {
      setStatus("error");
      setError("Something went wrong. Try again in a moment.");
    }
  };

  if (status === "done") {
    return (
      <div
        data-testid={`waitlist-success-${location}`}
        className="flex items-center gap-2 text-sm font-mono text-cyan-accent"
      >
        <Check className="w-4 h-4 shrink-0" strokeWidth={2.5} />
        You're on the list. We'll email you once {BUILD_LABEL[key]} ships.
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={`waitlist-form-${location}`}
      className="w-full"
    >
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status === "error") setStatus("idle");
          }}
          placeholder="you@example.com"
          aria-label="Email address"
          data-testid={`waitlist-input-${location}`}
          className="flex-1 h-11 px-3 rounded-sm bg-pg-surface border border-pg font-mono text-sm text-white placeholder:text-[var(--pg-text-muted)] focus:outline-none focus:border-[var(--pg-cyan)]"
        />
        <Button
          type="submit"
          disabled={status === "loading"}
          data-testid={`waitlist-submit-${location}`}
          className="h-11 px-5 rounded-sm font-display font-bold tag-label text-black hover:opacity-90 disabled:opacity-60"
          style={{ backgroundColor: "var(--pg-cyan)" }}
        >
          {status === "loading" ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Adding
            </>
          ) : (
            <>
              Notify me
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
      {status === "error" && (
        <p
          data-testid={`waitlist-error-${location}`}
          className="mt-2 text-xs font-mono text-[var(--pg-orange)]"
        >
          {error}
        </p>
      )}
    </form>
  );
};

export default WaitlistForm;
